import { generateSdkPassword } from './sdk-password';
import { WizardState, ApplicationState, DetailsState, DeploymentState, KafkaState } from './interfaces';
import type { SelectProps } from '@cloudscape-design/components/select';
import Deployment from './stepComponents/step3';
import { CHART_VERSION, FLINK_VERSION, CHECKPOINT_MODE_OPTIONS } from './steps-config';

export interface WizardInput {
    state: {
        application: ApplicationState;
        details: Record<string, any>;
        advanced?: Record<string, any>;
        deployment?: Record<string, any>;
        kafka?: Record<string, any>;
      };
    }

export function mapJsonToWizardState(raw: WizardInput): WizardState {
    const state = raw.state ?? {};
    const detailsSource = state.details ?? {};
    const appSource = state.application ?? {};
    const deploymemtSource = state.deployment ?? {};
    const kafkaSource = state.kafka ?? {};
   
    const getDefaultOption = (optionGroups: any[]): any => optionGroups[0]?.options?.[0] ?? {};

    const getOption = (obj: any, optionGroups: any[] = []): SelectProps.Option => {
      const source = obj?.options?.[0] ?? obj ?? {};
      const defaultOption = getDefaultOption(optionGroups);
      const value = source?.value ?? defaultOption?.value ?? '';
      const label = source?.label ?? defaultOption?.label ?? '';
      const options = optionGroups.flatMap(group => group?.options ?? []);
      const matched = options.find(option =>
        (value && option.value === value) ||
        (label && option.label === label)
      );
      const fallback = matched ?? defaultOption;

      return {
        value: value || fallback?.value || '',
        label: label || fallback?.label || '',
        resource_value: source?.resource_value || fallback?.resource_value || '',
      } as SelectProps.Option;
    };
  
    const details: DetailsState = {
      taskManager_instance_class: getOption(detailsSource.taskManager_instance_class),
      taskManager_availability_zone: getOption(detailsSource.taskManager_availability_zone),
      taskManager_resources_requests_memory: detailsSource.taskManager_resources_requests_memory ?? '',
      taskManager_resources_requests_cpu: detailsSource.taskManager_resources_requests_cpu ?? '',
      taskManager_resources_limits_memory: detailsSource.taskManager_resources_limits_memory ?? '',
      taskManager_resources_limits_cpu: detailsSource.taskManager_resources_limits_cpu ?? '',
      taskManager_nodepool: getOption(detailsSource.taskManager_nodepool),
      taskManager_scale_average_utilization: detailsSource.taskManager_scale_average_utilization,
      taskManager_scale_max_Replicas: detailsSource.taskManager_scale_max_Replicas,
      taskManager_scale_min_replicas: detailsSource.taskManager_scale_min_replicas,
      taskManager_scale_enabled: detailsSource.taskManager_scale_enabled ?? 'false',
      taskManager_instance_size: getOption(detailsSource.taskManager_instance_size),
      jobManager_instance_class: getOption(detailsSource.jobManager_instance_class),
      jobManager_instance_size: getOption(detailsSource.jobManager_instance_size),
      jobManager_availability_zone: getOption(detailsSource.jobManager_availability_zone),
      jobManager_resources_requests_memory: detailsSource.jobManager_resources_requests_memory ?? '',
      jobManager_resources_requests_cpu: detailsSource.jobManager_resources_requests_cpu ?? '',
      jobManager_resources_limits_memory: detailsSource.jobManager_resources_limits_memory ?? '',
      jobManager_resources_limits_cpu: detailsSource.jobManager_resources_limits_cpu ?? '',
      jobManager_nodepool: getOption(detailsSource.jobManager_nodepool),
      taskManager_number_of_task_slots: detailsSource.taskManager_number_of_task_slots ?? '',
      storage_access_mode: detailsSource.storage_access_mode ?? '',
      storage_class_name: detailsSource.storage_class_name ?? '',
      checkpoint_enabled: detailsSource.checkpoint_enabled ?? 'false',
      checkpoint_s3_dir: detailsSource.checkpoint_s3_dir ?? '',
      savepoint_s3_dir: detailsSource.savepoint_s3_dir ?? '',
      checkpoint_interval_ms: detailsSource.checkpoint_interval_ms ?? '300000',
      checkpoint_timeout_ms: detailsSource.checkpoint_timeout_ms ?? '3600000',
      checkpoint_mode: getOption(detailsSource.checkpoint_mode, CHECKPOINT_MODE_OPTIONS),
    };
  
    const application: ApplicationState = {
      organisationName: appSource.organisationName ?? '',
      applicationName: appSource.applicationName ?? '',
      environment: getOption(appSource.environment) ?? '',
      flink_version: getOption(appSource.flink_version, FLINK_VERSION),
      streaming_chart_version: getOption(appSource.streaming_chart_version, CHART_VERSION),
      // `||`, not `??`: an empty saved password is as broken as a missing one.
      password: appSource.password || generateSdkPassword(),
    };
  
    const deployment: DeploymentState = {
      containers_image: deploymemtSource.containers_image ?? '',
      containers_tag: deploymemtSource.containers_tag ?? '',
      containers_name: deploymemtSource.containers_name ?? '',
      resources_limits_cpu: deploymemtSource.resources_limits_cpu ?? '',
      resources_limits_memory: deploymemtSource.resources_limits_memory ?? '',
      resources_requests_cpu: deploymemtSource.resources_requests_cpu ?? '',
      resources_requests_memory: deploymemtSource.resources_requests_memory ?? '',
      parallelism: deploymemtSource.parallelism ?? '',
      number_of_workers: deploymemtSource.number_of_workers ?? '',
      entry_point: deploymemtSource.entry_point ?? '',
      storage_class_name: deploymemtSource.storage_class_name ?? '',
    };
  
    const kafka: KafkaState = {
      consumer_topic : kafkaSource.consumer_topic ?? '',
      consumer_spec_partitions : kafkaSource.consumer_spec_partitions ?? '',
      consumer_spec_replicas : kafkaSource.consumer_spec_replicas ?? '',
      consumer_spec_config_retention_ms : kafkaSource.consumer_spec_config_retention_ms ?? '',
      consumer_spec_segment_bytes : kafkaSource.consumer_spec_segment_bytes ?? '',
      consumer_topic_mode : kafkaSource.consumer_topic_mode ?? 'existing',      
      producer_topic_mode : kafkaSource.producer_topic_mode ?? 'existing',
      producer_topic : kafkaSource.producer_topic ?? '',
      producer_spec_partitions : kafkaSource.producer_spec_partitions ?? '',
      producer_spec_replicas : kafkaSource.producer_spec_replicas ?? '',
      producer_spec_config_retention_ms : kafkaSource.producer_spec_config_retention_ms ?? '',
      producer_spec_segment_bytes : kafkaSource.producer_spec_segment_bytes ?? '',
    };

    return {
      application,
      details,
      deployment,
      kafka,
      review: undefined,
    };
}
